import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Input, Spin, Tag } from 'antd';
import { CustomerServiceOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import { collectSupportContext } from './context';
import { renderSupportCard } from './cards';
import { setCustomerServiceEnabled } from './availability';
import { useFloatingAction } from '../FloatingActions';
import type { SuggestAction, SupportCard, SupportMessage, SupportOpenEventDetail } from './types';
import './index.css';

const DEFAULT_ENDPOINT = '/api/customer-service/chat';
const CONFIG_ENDPOINT = '/api/customer-service/config';
const SUPPORT_EVENT = 'imgcloud:open-support';

const getEndpoint = () => {
  return (window as any).IMG_CLOUD_SUPPORT_CHAT_URL
    || process.env.REACT_APP_CUSTOMER_SERVICE_CHAT_URL
    || DEFAULT_ENDPOINT;
};

const getConversationId = () => {
  const key = 'imgcloud_customer_service_conversation_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(key, id);
  }
  return id;
};

const initialMessage: SupportMessage = {
  role: 'assistant',
  content: '你好，我是图床小镇客服助手。可以帮你排查上传失败、Trace ID、VIP/支付、API Token、Web Hosting 和文件管理问题。',
  suggestions: [
    { id: 'upload', emoji: '🖼️', title: '上传失败怎么办？' },
    { id: 'trace', emoji: '🧭', title: 'Trace ID 有什么用？' },
    { id: 'vip', emoji: '💎', title: 'VIP 有哪些权益？' },
    { id: 'api', emoji: '🔑', title: 'API Token 怎么用？' },
  ],
};

const CustomerServiceWidget: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<SupportMessage[]>([initialMessage]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [extraContext, setExtraContext] = useState<Record<string, unknown>>({});
  const conversationId = useMemo(getConversationId, []);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(CONFIG_ENDPOINT, { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : { enabled: false }))
      .then((config) => {
        if (!cancelled) {
          setEnabled(config?.enabled === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEnabled(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCustomerServiceEnabled(enabled);
    if (!enabled) {
      abortRef.current?.abort();
      setOpen(false);
      setLoading(false);
      setCurrentStep('');
    }
  }, [enabled]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentStep]);

  const sendMessage = useCallback(async (text: string, contextOverride?: Record<string, unknown>) => {
    const content = text.trim();
    if (!enabled || !content || loading) return;

    setOpen(true);
    setInput('');
    setLoading(true);
    setCurrentStep('正在连接客服助手...');
    setMessages((prev) => [...prev, { role: 'user', content }, { role: 'assistant', content: '', cards: [] }]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const context = await collectSupportContext({ ...extraContext, ...(contextOverride || {}) });
      const response = await fetch(getEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'makers-conversation-id': conversationId,
        },
        body: JSON.stringify({ message: content, locale: 'zh', context }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`客服服务请求失败 (${response.status})`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('客服服务没有返回流式内容');

      const decoder = new TextDecoder();
      let buffer = '';
      const cards: SupportCard[] = [];
      let suggestions: SuggestAction[] = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (!payload || payload === '[DONE]') continue;
          try {
            const event = JSON.parse(payload);
            if (event.type === 'workflow_step') {
              setCurrentStep(event.label || event.step || '处理中...');
            }
            if (event.type === 'card') {
              cards.push({ type: event.cardType, data: event.data });
              const cardSnapshot = [...cards];
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last.role === 'assistant') last.cards = cardSnapshot;
                return copy;
              });
            }
            if (event.type === 'ai_response') {
              const nextContent = event.content || '';
              const cardSnapshot = [...cards];
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last.role === 'assistant') {
                  last.content = nextContent;
                  last.cards = cardSnapshot;
                }
                return copy;
              });
            }
            if (event.type === 'suggest_actions' && Array.isArray(event.actions)) {
              suggestions = event.actions;
            }
          } catch {
            // Ignore malformed stream chunks.
          }
        }
      }

      if (suggestions.length > 0) {
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last.role === 'assistant') last.suggestions = suggestions;
          return copy;
        });
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last.role === 'assistant') last.content = `客服助手暂时不可用：${(error as Error).message}`;
          return copy;
        });
      }
    } finally {
      setLoading(false);
      setCurrentStep('');
      abortRef.current = null;
    }
  }, [conversationId, enabled, extraContext, loading]);

  useEffect(() => {
    const handler = (event: Event) => {
      if (!enabled) return;
      const detail = (event as CustomEvent<SupportOpenEventDetail>).detail || {};
      setOpen(true);
      if (detail.context) setExtraContext((prev) => ({ ...prev, ...detail.context }));
      if (detail.message) {
        if (detail.autoSend !== false) {
          sendMessage(detail.message, detail.context || {});
        } else {
          setInput(detail.message);
        }
      }
    };
    window.addEventListener(SUPPORT_EVENT, handler);
    return () => window.removeEventListener(SUPPORT_EVENT, handler);
  }, [enabled, sendMessage]);

  const stop = () => {
    abortRef.current?.abort();
    setLoading(false);
    setCurrentStep('');
  };

  const submit = () => sendMessage(input);
  const supportAction = useMemo(() => (
    enabled
      ? {
          key: 'customer-service',
          order: 30,
          icon: <CustomerServiceOutlined />,
          label: '图床小镇客服',
          onClick: () => setOpen(true),
          testId: 'customer-service-entry',
        }
      : null
  ), [enabled]);

  useFloatingAction(supportAction);

  if (!enabled) {
    return null;
  }

  return (
    <>
      <Drawer
        title="图床小镇客服"
        placement="right"
        width={420}
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose={false}
        className="customer-service-drawer"
      >
        <div className="customer-service-panel">
          <div className="customer-service-messages">
            {messages.map((message, index) => (
              <div key={index} className={`customer-service-message customer-service-message-${message.role}`}>
                <div className="customer-service-bubble">
                  <div className="customer-service-content">{message.content}</div>
                  {message.cards?.map((card, cardIndex) => renderSupportCard(card, cardIndex))}
                </div>
                {message.suggestions && message.suggestions.length > 0 && !loading && (
                  <div className="customer-service-suggestions">
                    {message.suggestions.map((item) => (
                      <Tag key={item.id} className="customer-service-suggestion" onClick={() => sendMessage(item.action || item.title)}>
                        {item.emoji ? `${item.emoji} ` : ''}{item.title}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="customer-service-loading"><Spin size="small" /> <span>{currentStep || '处理中...'}</span></div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="customer-service-input">
            <Input.TextArea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onPressEnter={(event) => {
                if (!event.shiftKey) {
                  event.preventDefault();
                  submit();
                }
              }}
              autoSize={{ minRows: 1, maxRows: 4 }}
              placeholder="描述问题，或粘贴上传错误 / Trace ID"
              disabled={loading}
            />
            {loading ? (
              <Button danger icon={<StopOutlined />} onClick={stop} />
            ) : (
              <Button type="primary" icon={<SendOutlined />} onClick={submit} disabled={!input.trim()} />
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default CustomerServiceWidget;
